import { useEffect, useCallback, useState, useMemo } from 'react';
import Cookies from 'js-cookie';
import cloneDeep from 'lodash/cloneDeep';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { sortCategoriesByPriority } from '@yme/components/Header/Search/helpers';
import { getFacetProperties, getCurrentCategoryFacets, mergeFacetsCounts, updateFacets, } from '@yme/components/SearchPage/hooks/helpers';
import { usePrevious } from '@yme/hooks/usePrevious';
import { getBloomreachModel, getIsInAuthor } from '@yme/selectors/common';
import { Network } from '@yme/services/network';
import { BR_ID_COOKIE, FacetFields, RequestType, SearchType, getSearchParams, getPrimaryTypeFacet, CONTENT_SEARCH_CATEGORIES, PRODUCT_SEARCH_CATEGORIES, SearchParams, PixelPageView, setPixelPageView, } from '@yme/utils/bloomreach';
import { getFacetCountByName } from '@yme/utils/bloomreach/helpers';
import { PRODUCT_FL_PARAMS, CONTENT_FL_PARAMS } from './constants';
const initialProductData = {
    unit: {
        docs: [],
        facets: [],
        numFound: 0,
        isLoaded: false,
    },
    accessory: {
        docs: [],
        facets: [],
        numFound: 0,
        isLoaded: false,
    },
    apparel: {
        docs: [],
        facets: [],
        numFound: 0,
        isLoaded: false,
    },
};
const initialContentData = {
    destinations: {
        docs: [],
        numFound: 0,
        isLoaded: false,
    },
    news: {
        docs: [],
        numFound: 0,
        isLoaded: false,
    },
    event: {
        docs: [],
        numFound: 0,
        isLoaded: false,
    },
    other: {
        docs: [],
        numFound: 0,
        isLoaded: false,
    },
};
export function useSearchPage({ isMobileView }) {
    const { replace } = useHistory();
    const { query, category, sort, unit_type, collection, type, subcategories, gender, colors, sizes, is_discounted, } = getSearchParams();
    const prevQuery = usePrevious(query);
    const prevCategory = usePrevious(category);
    const { searchHost, accountId, productCatalog, contentCatalog } = useSelector(getBloomreachModel);
    const isInAuthor = useSelector(getIsInAuthor);
    const [productPrimaryFacets, setProductPrimaryFacets] = useState([]);
    const [contentPrimaryFacets, setContentPrimaryFacets] = useState([]);
    const [contentData, setContentData] = useState(cloneDeep(initialContentData));
    const [productData, setProductData] = useState(cloneDeep(initialProductData));
    const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(!query);
    const [isLoading, setIsLoading] = useState(false);
    const [autoCorrectQueryResult, setAutoCorrectQueryResult] = useState('');
    const [didYouMeanOptions, setDidYouMeanOptions] = useState([]);
    const primaryFacets = useMemo(() => {
        const mergedPrimaryFacets = [...productPrimaryFacets, ...contentPrimaryFacets];
        return sortCategoriesByPriority(mergedPrimaryFacets);
    }, [contentPrimaryFacets, productPrimaryFacets]);
    const getCommonRequestParams = useCallback(() => ({
        _br_uid_2: Cookies.get(BR_ID_COOKIE),
        account_id: accountId,
        'facet.field': FacetFields.PRIMARY_TYPE,
        ref_url: location.href,
        request_type: RequestType.SEARCH,
        search_type: SearchType.KEYWORD,
        url: location.href,
    }), [accountId]);
    const getProductData = useCallback(async ({ query, primaryType = '', facets = {}, sortingOrder, start = 0, hasSpinner = false, isInitialRequest = false, }) => {
        if (hasSpinner) {
            setIsLoading(true);
        }
        try {
            const productAnswer = await Network.get(searchHost, {
                params: {
                    ...getCommonRequestParams(),
                    start,
                    q: query,
                    rows: isMobileView ? 8 : 9,
                    fl: PRODUCT_FL_PARAMS.join(','),
                    domain_key: productCatalog,
                    fq: primaryType ? getFacetProperties(primaryType, facets) : undefined,
                    sort: sortingOrder || undefined,
                },
            });
            const primaryFacets = getPrimaryTypeFacet(productAnswer.data.facet_counts.facets);
            if (!primaryType || Object.values(facets).every(isEmpty)) {
                setProductPrimaryFacets(primaryFacets);
            }
            else {
                setProductPrimaryFacets((prev) => {
                    const currentPrimaryFacet = prev.find((item) => item.name === primaryType);
                    const primaryTypeFacet = getPrimaryTypeFacet(productAnswer.data.facet_counts.facets);
                    if (currentPrimaryFacet) {
                        currentPrimaryFacet.count = getFacetCountByName(primaryTypeFacet, primaryType);
                    }
                    return prev;
                });
            }
            setProductData((prev) => {
                const currentProductItem = prev[primaryType];
                if (currentProductItem) {
                    currentProductItem.docs =
                        start === 0
                            ? productAnswer.data.response.docs
                            : currentProductItem.docs.concat(productAnswer.data.response.docs);
                    currentProductItem.numFound = productAnswer.data.response.numFound;
                    const currentFacets = updateFacets(getCurrentCategoryFacets(productAnswer.data.facet_counts.facets, primaryType));
                    currentProductItem.facets = currentProductItem.facets.length
                        ? mergeFacetsCounts(currentProductItem.facets, currentFacets)
                        : currentFacets;
                    currentProductItem.isLoaded = true;
                    return {
                        ...cloneDeep(initialProductData),
                        [primaryType]: currentProductItem,
                    };
                }
                else {
                    return prev;
                }
            });
            if (isInitialRequest) {
                setAutoCorrectQueryResult(productAnswer.data.autoCorrectQuery ?? '');
                setDidYouMeanOptions(productAnswer.data.did_you_mean);
            }
            return {
                primaryFacets,
                autoCorrectQuery: productAnswer.data.autoCorrectQuery ?? '',
            };
        }
        catch (error) {
            console.error(error);
        }
        finally {
            if (hasSpinner) {
                setIsLoading(false);
            }
        }
    }, [searchHost, getCommonRequestParams, isMobileView, productCatalog]);
    const getContentData = useCallback(async (query, primaryType, start = 0, hasSpinner = false) => {
        if (hasSpinner) {
            setIsLoading(true);
        }
        try {
            const contentAnswer = await Network.get(searchHost, {
                params: {
                    ...getCommonRequestParams(),
                    start,
                    q: query,
                    rows: 12,
                    fl: CONTENT_FL_PARAMS.join(','),
                    catalog_name: contentCatalog,
                    fq: primaryType ? `primary_type: "${primaryType}"` : undefined,
                },
            });
            const primaryFacets = getPrimaryTypeFacet(contentAnswer.data.facet_counts.facets);
            setContentPrimaryFacets(primaryFacets);
            setContentData((prev) => {
                const currentContentItem = prev[primaryType];
                if (currentContentItem) {
                    currentContentItem.docs = currentContentItem.docs.concat(contentAnswer.data.response.docs);
                    currentContentItem.numFound = contentAnswer.data.response.numFound;
                    currentContentItem.isLoaded = true;
                    return {
                        ...prev,
                        [primaryType]: currentContentItem,
                    };
                }
                else {
                    return prev;
                }
            });
            return primaryFacets;
        }
        catch (error) {
            console.error(error);
        }
        finally {
            if (hasSpinner) {
                setIsLoading(false);
            }
        }
    }, [searchHost, getCommonRequestParams, contentCatalog]);
    const changeTab = useCallback((facets, category) => {
        if (facets.length !== 0 && facets.every((item) => item.name !== category)) {
            const queryParams = new URLSearchParams(window.location.search);
            const currentCategory = head(facets)?.name ?? '';
            queryParams.set(SearchParams.CATEGORY, currentCategory);
            replace({
                search: queryParams.toString(),
            });
        }
    }, [replace]);
    useEffect(() => {
        if (isInitialDataLoaded &&
            productCatalog &&
            contentCatalog &&
            category === prevCategory &&
            (category || primaryFacets.length === 0) &&
            !isInAuthor) {
            const catalogName = PRODUCT_SEARCH_CATEGORIES.includes(category)
                ? productCatalog
                : contentCatalog;
            setPixelPageView(PixelPageView.SEARCH, {
                catalogs: [
                    {
                        name: catalogName,
                    },
                ],
                domain_key: productCatalog,
                search_term: query,
            });
        }
    }, [
        query,
        category,
        sort,
        unit_type,
        collection,
        type,
        subcategories,
        gender,
        colors,
        sizes,
        is_discounted,
        productCatalog,
        contentCatalog,
        isInitialDataLoaded,
        prevCategory,
        primaryFacets.length,
        isInAuthor,
    ]);
    useEffect(() => {
        if (query) {
            setContentData(cloneDeep(initialContentData));
            setProductData(cloneDeep(initialProductData));
            setProductPrimaryFacets([]);
            setContentPrimaryFacets([]);
            setIsInitialDataLoaded(false);
            setAutoCorrectQueryResult('');
        }
    }, [query]);
    useEffect(() => {
        const getSearchData = async (query, category) => {
            const isContentCategory = CONTENT_SEARCH_CATEGORIES.includes(category);
            try {
                const [product, content] = await Promise.allSettled([
                    getProductData({
                        query,
                        isInitialRequest: true,
                    }),
                    getContentData(query, isContentCategory ? category : ''),
                ]);
                let productFacets = [];
                let autoCorrectQuery = '';
                let contentFacets = content.status === 'fulfilled' && content.value ? content.value : [];
                if (product.status === 'fulfilled' && product.value) {
                    productFacets = product.value.primaryFacets;
                    autoCorrectQuery = product.value.autoCorrectQuery;
                }
                if (autoCorrectQuery) {
                    contentFacets =
                        (await getContentData(autoCorrectQuery, isContentCategory ? category : '')) ?? [];
                }
                changeTab(sortCategoriesByPriority([...productFacets, ...contentFacets]), category);
                setIsInitialDataLoaded(true);
            }
            catch (error) {
                setIsInitialDataLoaded(true);
                console.error(error);
            }
        };
        if (query &&
            searchHost &&
            contentCatalog &&
            productCatalog &&
            !isInitialDataLoaded &&
            category === prevCategory) {
            getSearchData(query, category);
        }
    }, [
        query,
        category,
        prevCategory,
        searchHost,
        isInitialDataLoaded,
        getProductData,
        getContentData,
        changeTab,
        contentCatalog,
        productCatalog,
    ]);
    useEffect(() => {
        if (!isInitialDataLoaded || query !== prevQuery) {
            return;
        }
        if (PRODUCT_SEARCH_CATEGORIES.includes(category)) {
            getProductData({
                query,
                primaryType: category,
                sortingOrder: sort,
                facets: {
                    unit_type,
                    collection,
                    type,
                    subcategories,
                    gender,
                    colors,
                    sizes,
                    is_discounted,
                },
                hasSpinner: true,
            });
        }
        if (CONTENT_SEARCH_CATEGORIES.includes(category) &&
            contentData[category].isLoaded === false) {
            getContentData(autoCorrectQueryResult || query, category, 0, true);
        }
    }, [
        category,
        collection,
        contentData,
        getContentData,
        getProductData,
        isInitialDataLoaded,
        prevQuery,
        query,
        sort,
        unit_type,
        type,
        subcategories,
        gender,
        colors,
        sizes,
        is_discounted,
        autoCorrectQueryResult,
    ]);
    return {
        isInitialDataLoaded,
        isLoading,
        category,
        query,
        primaryFacets,
        productData,
        contentData,
        autoCorrectQueryResult,
        didYouMeanOptions,
        getProductData,
        getContentData,
    };
}
